import React, { useState, useEffect } from 'react';
import { Column, SortConfig } from './GridConfigurator';

interface ColumnConfiguratorProps {
  isAdmin: boolean;
  availableFields: string[];
  columns: Column[];
  sortConfig: SortConfig;
  onColumnChange: (newColumns: Column[], newSortConfig: SortConfig) => void;
}

const ColumnConfigurator: React.FC<ColumnConfiguratorProps> = (
  {
    isAdmin,
    availableFields,
    columns,
    sortConfig,
    onColumnChange,
  }) => {
  const [editingColumns, setEditingColumns] = useState<Column[]>([...columns]);
  const [editingSortConfig, setEditingSortConfig] = useState<SortConfig>(sortConfig);

  useEffect(() => {
    onColumnChange(editingColumns, editingSortConfig);
  }, [editingColumns, editingSortConfig]);

  const toTitleCase = (input: string): string => {
    if (!input) return '';

    return input
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\./g, ' ')
      .toLowerCase()
      .replace(/^./, c => c.toUpperCase());
  };

  const handleAdd = () => setEditingColumns([...editingColumns, { fieldName: '', caption: '', width: 0, isVisible: true, isRequired: false }]);

  const handleRemove = (index: number) => {
    const newCols = [...editingColumns];
    newCols.splice(index, 1);
    setEditingColumns(newCols);
  };

  const handleChange = (index: number, key: keyof Column, value: string) => {
    const newCols = [...editingColumns];
    if (key === 'fieldName' && value && !newCols[index].isRequired) {
      newCols[index].fieldName = value;
      newCols[index].caption = toTitleCase(value);
    } else if (key === 'caption' && value) {
      newCols[index].caption = value;
    } else if (key === 'width') {
      newCols[index].width = parseInt(value, 10);
    } else if (key === 'isVisible') {
      newCols[index].isVisible = value === 'true';
    } else if (key === 'isRequired') {
      newCols[index].isRequired = value === 'true';
      if (newCols[index].isRequired) {
        newCols[index].isVisible = true;
      }
    }
    setEditingColumns(newCols);
  };

  const handleSortChange = (key: 'fieldName' | 'direction', value: string) =>
    setEditingSortConfig(prev => ({
      ...prev,
      [key]: value || null,
    }));

  const handleDragStart = (e: React.DragEvent<HTMLSpanElement>, index: number) => {
    if (e.target instanceof HTMLElement && e.target.classList.contains('drag-handle')) {
      e.dataTransfer.setData('text/plain', index.toString());
    } else {
      e.preventDefault();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (sourceIndex === targetIndex) return;
    const newCols = [...editingColumns];
    const [movedCol] = newCols.splice(sourceIndex, 1);
    newCols.splice(targetIndex, 0, movedCol);
    setEditingColumns(newCols);
  };

  const getAvailableFieldsFor = (currentField: string, isRequired: boolean) =>
    isRequired
      ? [currentField] // Only allow the current field if required
      : availableFields.filter(
        f => f === currentField || !editingColumns.some(c => c.fieldName === f)
      );

  const isSaveDisabled = editingColumns.some(
    col => !col.fieldName || !col.caption
  );

  return <>
    <table className="table table-bordered">
      <thead>
      <tr>
        <th></th>
        <th>{window.ifunds.strings.show}</th>
        <th>{window.ifunds.strings.fieldName}</th>
        {isAdmin ? <th>{window.ifunds.strings.caption}</th> : undefined}
        <th>{window.ifunds.strings.width}</th>
        {isAdmin ? <th>{window.ifunds.strings.required}</th> : undefined}
        {isAdmin ? <th></th> : undefined}
      </tr>
      </thead>
      <tbody>
      {editingColumns.map((col, index) => <tr
        key={index}
        onDragOver={handleDragOver}
        onDrop={e => handleDrop(e, index)}
      >
        <td className="align-middle w-0">
          <span
            className="drag-handle"
            style={{ cursor: 'grab' }}
            draggable
            onDragStart={e => handleDragStart(e, index)}
          >
            ☰
          </span>
        </td>
        <td className="align-middle text-center w-0">
          <input
            onChange={e => handleChange(index, 'isVisible', e.target.checked ? 'true' : 'false')}
            checked={col.isVisible}
            className="form-check-input"
            type="checkbox"
            disabled={col.isRequired}
          />
        </td>
        <td>
          {isAdmin ?
            <select
              className="form-select"
              value={col.fieldName}
              onChange={e => handleChange(index, 'fieldName', e.target.value)}
              required
            >
              <option value="" disabled>
                Select Field
              </option>
              {getAvailableFieldsFor(col.fieldName, col.isRequired).map(field => <option key={field} value={field}>
                {field}
              </option>)}
            </select>
            :
            <>{col.caption}</>
          }
        </td>
        {isAdmin ?
          <td>
            <input
              type="text"
              className="form-control"
              value={col.caption}
              onChange={e => handleChange(index, 'caption', e.target.value)}
              placeholder="Caption"
              required
            />
          </td>
          : undefined
        }
        <td>
          <select
            className="form-select me-2"
            style={{ width: '100px' }}
            value={col.width}
            onChange={e => handleChange(index, 'width', e.target.value)}
          >
            {[0, 25, 50, 75, 100].map(w => <option key={w} value={w}>
              {w}%
            </option>)}
          </select>
        </td>
        {isAdmin ?
          <>
            <td className="align-middle text-center">
              <input
                onChange={e => handleChange(index, 'isRequired', e.target.checked ? 'true' : 'false')}
                checked={col.isRequired}
                className="form-check-input"
                type="checkbox"
              />
            </td>
            <td>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleRemove(index)}
              >
                <i className="fas fa-remove"></i>
              </button>
            </td>
          </>
          : undefined
        }
      </tr>)}
      {isAdmin ?
        <tr>
          <td colSpan={7}>
            <button style={{ width: 'max-content' }} type="button" className="btn btn-outline-primary" onClick={handleAdd}>
              {window.ifunds.strings.addColumn}
            </button>
          </td>
        </tr>
        : undefined
      }
      <tr>
        <td colSpan={2} className="align-middle">
          <strong>Sort</strong>
        </td>
        <td colSpan={isAdmin ? 5 : 2}>
          <div className="d-flex align-items-center">
            <select
              className="form-select me-2"
              value={editingSortConfig.fieldName || ''}
              onChange={e => handleSortChange('fieldName', e.target.value)}
            >
              <option value="">None</option>
              {editingColumns.filter(col => col.isVisible).map(col => <option key={col.fieldName} value={col.fieldName}>
                {col.caption}
              </option>)}
            </select>
            <select
              className="form-select"
              value={editingSortConfig.direction || ''}
              onChange={e => handleSortChange('direction', e.target.value)}
              disabled={!editingSortConfig.fieldName}
            >
              <option value="ascending">Ascending</option>
              <option value="descending">Descending</option>
            </select>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </>;
};

export default ColumnConfigurator;
